/**
 * PageLoading
 * Consistent loading states for admin pages.
 * Supports list, grid and form variants.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type LoadingVariant = 'list' | 'grid' | 'form' | 'simple';

interface PageLoadingProps {
  variant?: LoadingVariant;
  rows?: number;
  columns?: number;
  className?: string;
}

export function PageLoading({
  variant = 'simple',
  rows = 3,
  columns = 3,
  className,
}: PageLoadingProps) {
  if (variant === 'simple') {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-content-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
      </div>
    );
  }

  if (variant === 'form') {
    return (
      <div className={cn('space-y-6', className)}>
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'grid') {
    return (
      <div
        className={cn(
          'grid gap-4',
          columns === 2 && 'grid-cols-1 sm:grid-cols-2',
          columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
          columns === 4 && 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
          columns === 5 && 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5',
          className
        )}
      >
        {Array.from({ length: rows * columns }).map((_, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded overflow-hidden">
            <Skeleton className="aspect-[9/16] w-full" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // list variant
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}
